<template>
  <step-template v-if="videos.length > 0" :subStep="subStep">

    <v-row class="mx-5 mb-5 mt-0">
      <v-col :cols="`${!isMobile ? 3 : 12}`">
        <v-img
          max-height="250"
          max-width="250"
          :src="configLogoUrl"
          class="m-auto"
        ></v-img>
      </v-col>
      <v-col :cols="`${!isMobile ? 9 : 12}`">
        <v-col cols="12">
        <h2>
          Welcome to the BYOCS Video Library
        </h2> 
        <h5>
          Here you'll find video resources, some of
          which you saw in your patient case, as well as additional topics of
          interest. When you've completed reviewing these resources, click <span class="text-orange"><b>RETURN TO CASE</b></span>.
        </h5>
        <h5 class="mt-10">
          Browse the sections below to get started.
        </h5>
        <v-btn
          outlined
          color="orange"
          :rounded="true"
          class="border mt-4"
          @click="returnToSteps"
        ><v-icon left>mdi-arrow-left</v-icon>Return To Case</v-btn>
      </v-col>
      </v-col>
    </v-row>

    <!-- Regional Guidelines Section -->
    <v-row v-if="avatarResourcesBySections.length" class="mx-5 mb-5 mt-0">
      <v-col cols="12">
        <div class="section-header d-flex align-center mb-4">
          <h3 class="ma-0">Regional Guidelines &amp; Agencies</h3>
          <v-icon color="red lighten-2" class="ml-2">mdi-star</v-icon>
        </div>
        <p class="mb-6 text-gray-600"><span class="text-red">*</span><strong> Helpful information can also be found in the Downloadable Resources section of this course.</strong></p>
        <v-row>
          <v-col 
            v-for="(resource, rKey) in avatarResourcesBySections" 
            :key="rKey"
            :cols="12"
            :sm="6"
            :md="4"
            :lg="3"
          >
            <v-card outlined class="pa-4 h-100">
              <h6 class="ma-0">{{ resource.section }} - {{ resource.name }}</h6>
              <p class="mt-2 mb-4"><i>{{ resource.label }}</i></p>
              <avatar-resources
                buttonName="View Resource Now"
                :buttonUrl="resource.url"
              />
            </v-card>
          </v-col>
        </v-row>
        <v-divider class="mt-6"></v-divider>
      </v-col>
    </v-row>

    <!-- Video Sections -->
    <div 
      v-for="(section, sKey) in videosBySections" 
      :key="sKey" 
      class="section-box mx-5 mb-6"
    >
      <div class="section-header d-flex align-center">
        <h3 class="ma-0">{{ sKey }}</h3>
        <v-icon 
          v-if="sKey === 'Additional Topics of Interest' || sKey === 'The Buzz Series'" 
          color="red lighten-2" 
          class="ml-2"
        >mdi-star</v-icon>
      </div>
      <v-divider class="my-3"></v-divider>
      <v-list class="video-list pa-0">
        <v-list-item
          v-for="(video, vKey) in section"
          :key="vKey"
          class="video-list-item mb-2 pa-0"
          @click="openModal(video)"
        >
          <div class="video-row d-flex align-center">
            <div class="video-thumb-wrapper">
              <v-img
                :lazy-src="`https://fast.wistia.com/embed/medias/${video.id}/swatch`"
                :src="videosThumbs[video.id]"
                width="160"
                height="90"
                class="video-thumbnail rounded"
                cover
              >
                <div class="play-overlay d-flex align-center justify-center">
                  <v-icon color="white" size="36">mdi-play-circle</v-icon>
                </div>
              </v-img>
            </div>
            <div class="video-info ml-4 flex-grow-1">
              <span class="video-title">{{ video.title_public }}</span>
            </div>
            <v-icon class="video-chevron ml-2" color="grey">mdi-chevron-right</v-icon>
          </div>
        </v-list-item>
      </v-list>
    </div>
    <v-row class="ma-5">
      <v-btn
        outlined
        color="orange"
        :rounded="true"
        class="border"
        @click="returnToSteps"
        ><v-icon left>mdi-arrow-left</v-icon>Return To Case</v-btn
      >
    </v-row>
    <modal-video
      ref="modal-video"
      :user="user"
      :video="modal.video"
      :title="modal.title"
    />
  </step-template>
</template>

<script>
import { mapState, mapMutations, mapActions } from "vuex";
import axios from "axios";
import ModalVideo from "/src/components/modals/ModalVideo.vue";
import StepTemplate from "/src/components/steps/StepTemplate.vue";
import AvatarResources from "/src/components/buttons/AvatarResources.vue";
import caseConfigMixin from "/src/mixins/caseConfigMixin.js";

export default {
  name: "Library",
  mixins: [caseConfigMixin],
  components: {
    ModalVideo,
    StepTemplate,
    AvatarResources
  },
  props: {
    steps: {
      type: Array,
      required: true,
    },
    step: {
      type: Object,
      required: true,
      default: () => {
        return {};
      },
    },
    subStep: {
      type: Object,
      required: true,
      default: () => {
        return {};
      },
    },
    videos: {
      type: Array,
      required: true,
    },
    avatars: {
      type: Array,
      required: true,
    },
    isMobile: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      videosBySections: {},
      videosThumbs: {},
      avatarResourcesBySections: {},
      modal: {
        video: {},
        title: "",
      },
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.user,
      case: (state) => state.case,
    }),
  },
  watch: {
    videos(val) {
      this.setUpVideosBySections();
    },
  },
  beforeMount() {
    this.setUpVideosBySections();
    this.getAllAvatarResources();
    this.trackingAction("Library Panel Opened");
  },
  beforeDestroy() {
    this.trackingAction("Library Panel Closed");
  },
  methods: {
    ...mapMutations(["setShowLibrary"]),
    ...mapActions(["trackingAction"]),
    returnToSteps() {
      this.setShowLibrary(false);
    },
    openModal(video) {
      this.modal.title = video.title;
      this.modal.video = video;
      this.$refs["modal-video"].setVisible(true);
    },
    getAllAvatarResources() {
      let avatarResourcesList = [];
      for (const aKey in this.avatars) {
        const avatar = this.avatars[aKey];
        if (avatar.variables) {
          let avatarVariables = JSON.parse(avatar.variables);
          if (avatarVariables.resources) {
            for (const rKey in avatarVariables.resources) {
              avatarResourcesList.push(avatarVariables.resources[rKey]);
            }
          }
        }
      }
      console.log(avatarResourcesList);
      this.avatarResourcesBySections = avatarResourcesList;
      //this.setUpAvatarAvatarResourcesBySections();
    },
    setUpAvatarAvatarResourcesBySections () {
      /*
      if (this.avatarResourcesBySections.length === 0) {
        return;
      }
      const avatarResourcesSections = new Object;

      for (const key in this.avatarResourcesBySections) {
        const r = this.avatarResourcesBySections[key];

        // Add Type
        if (avatarResourcesSections[r.type] === undefined) {
          avatarResourcesSections[r.type] = {};
        }

        // Add Section
        if (avatarResourcesSections[r.type][r.section] === undefined) {
          avatarResourcesSections[r.type][r.section] = {};
        }
      }
      this.avatarResourcesBySections = avatarResourcesSections;
      */
    },
    setUpVideosBySections() {
      if (this.videos.length === 0) {
        return;
      }

      // Filter videos for current case and sort by library_order
      const caseVideos = this.videos
        .filter(v => v.id_case == this.case.id)
        .sort((a, b) => (a.library_order || 0) - (b.library_order || 0));

      // Build sections dynamically from library_section field
      const sections = {};
      for (const v of caseVideos) {
        const sectionName = v.library_section;
        if (!sectionName) continue;

        if (!sections[sectionName]) {
          sections[sectionName] = [];
        }
        sections[sectionName].push(v);
        this.thumbSrc(v.id);
      }

      this.videosBySections = sections;
    },
    thumbSrc(id) {
      var config = {
        method: "get",
        url: `https://fast.wistia.com/embed/medias/${id}.jsonp`,
        headers: {
          "Content-Type": "application/json",
        },
      };
      const self = this;
      axios(config)
        .then(function(response) {
          let data = response.data;
          data = data.split("= ")[1];
          data = data.split(";\nwindow['wistiajsonp")[0];
          data = data.slice(0, -2);
          try {
            const dataString = JSON.parse(data);
            self.$set(
              self.videosThumbs,
              id,
              `${dataString.media.embed_options.stillUrl}?image_crop_resized=480x270`
            );
          } catch (e) {
            // console.log(
            //   "Failed to fetch thumb",
            //   `https://fast.wistia.com/embed/medias/${id}.jsonp`
            // );
          }
        })
        .catch(function(error) {
          console.log(error);
        });
    },
  },
};
</script>

<style scoped>
.section-box {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.section-header h3 {
  color: #333;
  font-weight: 600;
}

.video-list {
  background: transparent;
}

.video-list-item {
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s;
}

.video-list-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.video-row {
  width: 100%;
  padding: 8px;
}

.video-thumb-wrapper {
  flex-shrink: 0;
}

.video-thumbnail {
  position: relative;
}

.play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  opacity: 0;
  transition: opacity 0.2s;
}

.video-list-item:hover .play-overlay {
  opacity: 1;
}

.video-title {
  font-size: 0.95rem;
  font-weight: 500;
  line-height: 1.4;
  color: #333;
}

.video-chevron {
  flex-shrink: 0;
}

@media (max-width: 600px) {
  .video-thumb-wrapper .v-image {
    width: 120px !important;
    height: 68px !important;
  }
  
  .video-title {
    font-size: 0.85rem;
  }
  
  .video-chevron {
    display: none;
  }
}
</style>
